import type { ApiClient } from '../api/client';
import { META, readMeta, writeMeta } from '../db/schema';
import type { AppDb } from '../db/schema';
import {
  LapsedRegulars,
  ReportSummary,
  RevenueByMonth,
  ServiceMix,
  TopClients,
  VisitsByWeekday,
} from '../wire';
import type {
  LapsedRegulars as LapsedRegularsType,
  ReportSummary as ReportSummaryType,
  RevenueByMonth as RevenueByMonthType,
  ServiceMix as ServiceMixType,
  TopClients as TopClientsType,
  VisitsByWeekday as VisitsByWeekdayType,
} from '../wire';

/**
 * The reports — **fetched, not mirrored**, and the distinction is the design.
 *
 * Everything in `mirror.ts` is the tenant's *working set*: rows the counter acts
 * on, which the device must be able to show and to change with no network at
 * all. A report is not that. It is a fold over the whole ledger — a year of
 * settlements, four dates of money, SQL that converts every `timestamptz` with
 * `AT TIME ZONE` — and there is exactly one place that fold is correct
 * (`packages/store/src/repos/reports.ts`). Recomputing revenue on the device
 * from a 60-day mirror would produce a second answer that is wrong in a way
 * nobody could see: it would simply be smaller.
 *
 * So this class does the one honest thing available offline: it keeps **the last
 * answer the server gave**, stamped with when it gave it, and the screen says so
 * in the same words the status strip uses for the book. A report the owner knows
 * is from Tuesday is useful. A report that silently rebuilds itself out of
 * whatever the device happens to hold is not.
 *
 * ## It is still not a view touching the network
 *
 * The rule the app is built on (`mirror.ts`) is that **reads render from local
 * storage** — `snapshot()` reads IndexedDB and only IndexedDB, exactly like
 * `mirror.day()`. `refresh()` is the other half, and it is called the way
 * `sync.start()` is called: from an effect when the screen opens, never on the
 * render path. Opening Reports offline paints the last snapshot immediately and
 * the refresh fails in the background, which is the same shape as every other
 * screen in the app.
 */

export interface ReportsSnapshot {
  /** When the server answered. The staleness line is computed from this. */
  fetchedAt: string;
  summary: ReportSummaryType;
  revenue: RevenueByMonthType;
  mix: ServiceMixType;
  weekdays: VisitsByWeekdayType;
  top: TopClientsType;
  lapsed: LapsedRegularsType;
}

export interface ReportsOptions {
  db: AppDb;
  api: ApiClient;
  onChange: () => void;
  now?: () => number;
}

export class Reports {
  private readonly db: AppDb;
  private readonly api: ApiClient;
  private readonly onChange: () => void;
  private readonly now: () => number;
  /** One refresh at a time; a second tap joins the first. */
  private inFlight: Promise<void> | null = null;

  constructor(options: ReportsOptions) {
    this.db = options.db;
    this.api = options.api;
    this.onChange = options.onChange;
    this.now = options.now ?? Date.now;
  }

  /** The last answer the server gave, or `null` if it has never given one. */
  async snapshot(): Promise<ReportsSnapshot | null> {
    return (await readMeta<ReportsSnapshot>(this.db, META.reports)) ?? null;
  }

  /**
   * Fetch all six, then store them as **one** snapshot.
   *
   * One snapshot rather than six, because they are read together and a mixture
   * of Tuesday's revenue and today's visit count is a screen that cannot state
   * its own age. `Promise.all` means one failure discards the batch — which is
   * the right outcome: a partial refresh would be exactly that mixture.
   */
  async refresh(): Promise<void> {
    if (this.inFlight !== null) return this.inFlight;
    const run = this.fetchAll().finally(() => {
      this.inFlight = null;
    });
    this.inFlight = run;
    return run;
  }

  private async fetchAll(): Promise<void> {
    const [summary, revenue, mix, weekdays, top, lapsed] = await Promise.all([
      this.api.request({
        method: 'GET',
        path: '/reports/summary',
        query: { days: 30 },
        schema: ReportSummary,
      }),
      this.api.request({
        method: 'GET',
        path: '/reports/revenue-by-month',
        query: { months: 12 },
        schema: RevenueByMonth,
      }),
      this.api.request({
        method: 'GET',
        path: '/reports/service-mix',
        query: { days: 90, kind: 'all', limit: 12 },
        schema: ServiceMix,
      }),
      this.api.request({
        method: 'GET',
        path: '/reports/visits-by-weekday',
        query: { days: 90 },
        schema: VisitsByWeekday,
      }),
      this.api.request({
        method: 'GET',
        path: '/reports/top-clients',
        query: { days: 365, limit: 8 },
        schema: TopClients,
      }),
      this.api.request({
        method: 'GET',
        path: '/reports/lapsed-regulars',
        query: { limit: 10 },
        schema: LapsedRegulars,
      }),
    ]);

    const snapshot: ReportsSnapshot = {
      fetchedAt: new Date(this.now()).toISOString(),
      summary,
      revenue,
      mix,
      weekdays,
      top,
      lapsed,
    };
    await writeMeta(this.db, META.reports, snapshot);
    this.onChange();
  }
}
